import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated } from '../../hooks/useAuth';
import { usePermissoes } from '../../hooks/usePermissoes';
import { isPermissaoDisponivel } from '../../types/usuarios';

// Mapa rota → permissão necessária. Match faz `startsWith` — `find` retorna
// o primeiro match. Ordem importa: as rotas mais específicas precisam vir
// antes de prefixos mais curtos. Como nenhum prefixo /compras genérico está
// listado, as 5 sub-rotas convivem sem conflito.
// Array = basta UMA das chaves (união). Usado quando uma tela hospeda áreas de
// permissões distintas e cada aba se esconde sozinha lá dentro.
const ROTA_PERMISSAO: Record<string, string | string[]> = {
  // '/' (HomePage) NÃO está aqui — qualquer usuário autenticado vê os cards
  // dos recursos disponíveis. Dashboard real fica em /dashboard.
  '/dashboard': 'dashboard',
  '/pedidos': 'pedidos',
  '/xml-terceiros': 'nfe_terceiros',
  '/cte-terceiros': 'cte_terceiros',
  // Tela de acompanhamento de pendências — união: quem tem só `conferencia_pendencia`
  // (supervisor sem a policy `conferencia_nfe`) também precisa entrar. Entra ANTES do
  // prefixo curto '/conferencia' (mais específico vence no `find` por startsWith); o
  // deep-link '/conferencia/pendencias/:id' (dialog de detalhe) herda por prefixo.
  '/conferencia/pendencias': ['conferencia_nfe', 'conferencia_pendencia'],
  '/conferencia': 'conferencia_nfe',
  '/gerarCatalogo': 'gerar_catalogo',
  '/catalogoDigital': 'catalogo_online',
  '/agrupamentos': 'agrupamentos',
  // Duas áreas na mesma tela: a lista de pessoas (gerenciar_usuarios) e a matriz
  // de papéis (gerenciar_papeis). Basta uma — a página esconde a aba que a chave
  // não cobre, então quem só define política de acesso entra e vê só os papéis.
  '/usuarios': ['gerenciar_usuarios', 'gerenciar_papeis'],
  '/auditoria': 'auditoria',
  '/explorador': 'explorador',
  // Compras — granulares por sub-módulo (espelha PERMISSOES + Sidebar)
  '/compras/reposicao': 'compras.reposicao',
  '/compras/fornecedores': 'compras.fornecedores',
  '/compras/marcas': 'compras.marcas',
  '/compras/analise': 'compras.analise',
  '/compras/sugestoes': 'compras.sugestoes',
  '/compras/cotacoes': 'compras.cotacao',
  '/compras/pedidos': 'compras.pedido',
  // Metas — chave própria desde 2026-05-21 (antes reaproveitava compras.pedido).
  // Permite delegar gestão de metas sem dar acesso a fechar pedidos. Backend
  // mantém compras.pedido na policy "compras.metas" por compatibilidade.
  // Dimensão e seu sub-fluxo (lote) vêm antes — find usa startsWith, ordem importa.
  '/compras/metas/dimensao/lote': 'compras.metas',
  '/compras/metas/dimensao': 'compras.metas',
  '/compras/metas': 'compras.metas',
};

// Rotas que exigem perfil ADMIN (configuração de sistema). O backend já protege
// os endpoints correspondentes com AdminPolicy — este guard apenas evita que o
// usuário comum chegue na tela e veja erro 403 em cada chamada.
// /auditoria saiu daqui em 2026-05-21 — agora é permissão granular "auditoria".
const ROTAS_ADMIN: string[] = [
  '/organizacao',
];

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useIsAuthenticated();
  const { temPermissao, isAdmin } = usePermissoes();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-zinc-500">Carregando...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const negado = () => (
    <div className="min-h-screen flex flex-col items-center justify-center text-center p-4">
      <div className="text-4xl mb-3">🔒</div>
      <h1 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">Acesso restrito</h1>
      <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
        Você não tem permissão para acessar esta página.
      </p>
      <a href="/" className="mt-4 text-sm text-blue-600 hover:underline">Voltar ao início</a>
    </div>
  );

  // Rotas só-ADMIN têm prioridade — checadas antes do mapa de permissões.
  if (ROTAS_ADMIN.some(r => location.pathname.startsWith(r)) && !isAdmin) {
    return negado();
  }

  // Verificar permissão da rota
  const rota = Object.keys(ROTA_PERMISSAO).find(r =>
    r === '/' ? location.pathname === '/' : location.pathname.startsWith(r)
  );

  const chavesDaRota = rota
    ? [ROTA_PERMISSAO[rota]].flat()
    : [];

  // Feature flag 'disabled' bloqueia mesmo pra ADMIN — tela em desenvolvimento
  // não deve ser acessível por URL direta. Com união, basta UMA chave disponível
  // pra tela existir (as abas internas somem sozinhas).
  if (rota && !chavesDaRota.some(isPermissaoDisponivel)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-4">
        <div className="text-4xl mb-3">🚧</div>
        <h1 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">Recurso em desenvolvimento</h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
          Este recurso ainda não está disponível na sua instalação.
        </p>
        <a href="/" className="mt-4 text-sm text-blue-600 hover:underline">Voltar ao início</a>
      </div>
    );
  }

  if (rota && !chavesDaRota.some(temPermissao)) {
    return negado();
  }

  return <>{children}</>;
};

export default ProtectedRoute;
